import { setCookie, getCookie } from '../../../core/static/js/helpers/utils.js'

export default class SizeGuide extends HTMLElement {
    constructor() {
      super();
      this.currentUnit = 'inches';
      this.productData = this.getAttribute('products');
      this.rangeLabelElement = null;
      this.lengthLabelElement = null;
      this.sizeTableBlock = null;
    }
  
    connectedCallback() {
      this.renderTemplate();
      this.setMetrologyUnitsfromCookies();
      
      // Add close button functionality
      const closeButton = this.querySelector('#closeSizeGuideBtn');
      if (closeButton) {
          closeButton.addEventListener('click', (e) => {
              e.preventDefault();
              const sizeGuideDropdown = this.querySelector('#sizeGuide');
              if (sizeGuideDropdown) {
                  sizeGuideDropdown.classList.add('out-of-sight');
                  sizeGuideDropdown.classList.remove('visible');
              }
          });
      }

      this.sizeModelWrapper = this.querySelector('.size-model-wrapper');
      this.modelNameElement = this.querySelector('#modelName');
      this.avatarChestElement = this.querySelector('#avatarChest');
      this.avatarWaistElement = this.querySelector('#avatarWaist');
      this.avatarBicepsElement = this.querySelector('#avatarBiceps');
      this.avatarHeightElement = this.querySelector('#avatarHeight');
      this.avatarImageElement = this.querySelector('#avatarImage');
      this.avatarSizeElement = this.querySelector('#avatarSize');
      this.initUnitSelector();
      this.generateSizeTable(this.currentUnit);
      this.updateAvatarMeasurements(this.currentUnit);
    }

    setMetrologyUnitsfromCookies() {
      // This function retrieves the metrology unit (inches or cms) from cookies.
      // If no metrology data is found in the cookies, it sets the default unit to 'inches'
      // and stores this information in the cookies.
      // If metrology data is found, it updates the current unit based on the stored information.
      let metrologyData = getCookie('metrologyData');
      if (!metrologyData) {
        this.currentUnit = 'inches';
        setCookie('metrologyInfo', JSON.stringify({unit: 'inches'}));
      }
      else {
        let metrologyInfo = JSON.parse(metrologyData);
        if (metrologyInfo) {
          this.currentUnit = metrologyInfo.unit ;
        }
      }
    }
  
    initUnitSelector() {
      var unitSelector = this.querySelector('.custom-select');
      unitSelector.value = this.currentUnit;
      if (unitSelector) {
        unitSelector.addEventListener('change', (e) => {
          this.currentUnit = e.target.value;
          this.generateSizeTable(this.currentUnit);
          this.updateAvatarMeasurements(this.currentUnit);
          this.dispatchEvent(new CustomEvent('MetrologyUnitChanged', {
            detail: {
              unit: this.currentUnit
            },
            bubbles: true // Add this line
          }));
        });
      }
    }
  

    generateSizeTable(unit='inches') {
      // This function generates a size table based on the provided unit (inches or centimeters).
      // It first checks if product data is available. If not, it returns early.
      // Then, it retrieves the table element with the ID 'sizeGuideLabelTable'.
      // If the table element is not found, it returns early.
      // Next, it clears any existing content in the table.
      // It creates a header row with three columns: Size, Chest Range, and Tee Length.
      // Each header cell contains a paragraph element with the appropriate text and class names.
      // Finally, it appends the header row to the table and updates the label elements and detailed size guide link.
      if (!this.productData) return;
      
      const table = document.getElementById('sizeGuideLabelTable');
      if (!table) return;


      // Clear existing table content
      while (table.firstChild) {
        table.removeChild(table.firstChild);
      }
  
      // Create header row
      const headerRow = document.createElement('tr');
      
      // Size header
      const sizeHeader = document.createElement('th');
      const sizeText = document.createElement('p');
      sizeText.className = 'footnote';
      sizeText.id = 'specsLabel';
      sizeText.textContent = 'Size';
      sizeHeader.appendChild(sizeText);
      
      // Range header
      const rangeHeader = document.createElement('th');
      const rangeText = document.createElement('p');
      rangeText.className = 'footnote';
      rangeText.id = 'rangeLabel';
      rangeText.textContent = 'Chest Range';
      rangeHeader.appendChild(rangeText);
      
      // Length header
      const lengthHeader = document.createElement('th');
      const lengthText = document.createElement('p');
      lengthText.className = 'footnote';
      lengthText.id = 'lengthLabel';
      lengthText.textContent = 'Tee Length';
      lengthHeader.appendChild(lengthText);
      
      headerRow.appendChild(sizeHeader);
      headerRow.appendChild(rangeHeader);
      headerRow.appendChild(lengthHeader);
      table.appendChild(headerRow);

      this.rangeLabelElement = this.querySelector('#rangeLabel');
      this.lengthLabelElement = this.querySelector('#lengthLabel');
      this.specsLabelElement = this.querySelector('#specsLabel');
      this.detailedSizeGuideLink = this.querySelector('#detailedSizeGuideLink');


      this.updateLabels();
  
      // Create data rows
      this.productData.products.forEach((size, index) => {
          const row = document.createElement('tr');
          row.className = 'anchor';
          
          // Size cell
          const sizeCell = document.createElement('td');
          const sizeData = document.createElement('p');
          sizeData.className = 'footnote';
          sizeData.textContent = size.size;
          sizeCell.appendChild(sizeData);
          
          // Range cell
          const rangeCell = document.createElement('td');
          const rangeData = document.createElement('p');
          rangeData.className = 'footnote';
          if (unit === 'inches') {
            var rangeInInches = size.range_in_inches;
            rangeData.textContent = rangeInInches;
          }
          else {
            var rangeInCms = size.range_in_cms;

            if (rangeInCms.includes('-')) {
              const [start, end] = rangeInCms.split('-').map(parseFloat);
              rangeInCms = `${Math.floor(start)} - ${Math.floor(end)}`;
            } else {
              const parsedRange = parseFloat(rangeInCms);
              rangeInCms = isNaN(parsedRange) ? rangeInCms : `${Math.floor(parsedRange)}`;
            }

            rangeData.textContent = rangeInCms;
          }
          rangeCell.appendChild(rangeData);
          
          // Length cell
          const lengthCell = document.createElement('td');
          const lengthData = document.createElement('p');
          lengthData.className = 'footnote';
          if (unit === 'inches') {
            var lengthInInches = size.length_in_inches;
            lengthData.textContent = lengthInInches;
          }
          else {
            var lengthInCms = size.length_in_cms;
            if (lengthInCms.includes('-')) {
              const [start, end] = lengthInCms.split('-').map(parseFloat);
              lengthInCms = `${Math.floor(start)} - ${Math.floor(end)}`;
            } else {
              const parsedLength = parseFloat(lengthInCms);
              lengthInCms = isNaN(parsedLength) ? lengthInCms : `${Math.floor(parsedLength)}`;
            }
            lengthData.textContent = lengthInCms;
          }
          lengthCell.appendChild(lengthData);
          
          row.appendChild(sizeCell);
          row.appendChild(rangeCell);
          row.appendChild(lengthCell);
          row.addEventListener('click', () => {
              const sizeChangeEvent = new CustomEvent('sizeSelected', {
                  detail: {
                      sku: size.sku,
                      size: size.size
                  },
                  bubbles: true
              });
              document.dispatchEvent(sizeChangeEvent);
              
              // Close the size guide dropdown
              const dropdown = this.querySelector('#sizeGuide');
              if (dropdown) {
                  dropdown.classList.add('out-of-sight');
                  dropdown.classList.remove('visible');
              }
          });
          table.appendChild(row);
      });
    }
    convertToFeetAndInches(inches) {
      // Check if input is convertible to a valid number
      const numInches = Number(inches);
      if (isNaN(numInches)) {
        return inches; // Return original input if not a valid number
      }
      
      const feet = Math.floor(numInches / 12);
      const remainingInches = numInches % 12;
      if (remainingInches === 0) {
        return `${feet}'`;
      }
      const formattedInches = Number.isInteger(remainingInches) ? Math.floor(remainingInches) : remainingInches.toFixed(1);
      return `${feet}'${formattedInches}"`;
    }
  
  
    updateAvatarMeasurements(unit='inches') {
      let avatarData = this.productData.size_guide_info;
      if (Array.isArray(avatarData) && avatarData.length > 0) {
        this.avatarChestElement.textContent = unit === 'inches' ? avatarData[0].avatar_chest_inches + '"' : avatarData[0].avatar_chest_cms + 'cm';
        this.avatarWaistElement.textContent = unit === 'inches' ? avatarData[0].avatar_waist_inches + '"' : avatarData[0].avatar_waist_cms + 'cm';
        this.avatarBicepsElement.textContent = unit === 'inches' ? avatarData[0].avatar_biceps_inches + '"' : avatarData[0].avatar_biceps_cms + 'cm';

        if (unit === 'inches') {
          var heightValue = this.convertFractionToDecimal(avatarData[0].avatar_height_inches);
          var heightInFeet = this.convertToFeetAndInches(heightValue);
          this.avatarHeightElement.textContent = heightInFeet;
        }
        else {
          this.avatarHeightElement.textContent = avatarData[0].avatar_height_cms + 'cm';
        }
        this.modelNameElement.textContent = avatarData[0].avatar_name;
        this.avatarImageElement.src = avatarData[0].comparison_image;
        this.avatarSizeElement.textContent = avatarData[0].avatar_size;
      }
      else{ 
        this.sizeModelWrapper.classList.add('out-of-sight');
      }
    }

    convertFractionToDecimal(value) {
      if (typeof value === 'number') return value;
      
      // Handle values like "7½"
      const fractionMap = {
        '½': 0.5,
        '¼': 0.25,
        '¾': 0.75
      };

      // Extract whole number and fraction
      const match = String(value).match(/(\d+)?([½⅓⅔¼¾⅕⅖⅗⅘])?/);
      if (!match) return parseFloat(value) || 0;

      const wholeNumber = match[1] ? parseInt(match[1]) : 0;
      const fraction = match[2] ? fractionMap[match[2]] : 0;

      return wholeNumber + fraction;
    }

    updateLabels() {
      // This function updates the labels for range, length, and specs based on the metrology information
      // from the product data. It also handles the display of a detailed size guide link if available.
      let avatarData = this.productData.metrology_info;
      let rangeLabel = avatarData.range_label;
      let lengthLabel = avatarData.length_label;
      let specsLabel = avatarData.specs_label;
      this.rangeLabelElement.textContent = rangeLabel;
      this.lengthLabelElement.textContent = lengthLabel;
      this.specsLabelElement.textContent = specsLabel;
      let showDetailedSizeGuideLink = avatarData.detailed_size_guide;
      let detailedSizeGuideLink = this.productData.sizeGuideUrl;
      if (showDetailedSizeGuideLink && detailedSizeGuideLink) {
        this.detailedSizeGuideLink.style.display = 'inline-block';
        this.detailedSizeGuideLink.href = detailedSizeGuideLink;

      }
      else {
        this.detailedSizeGuideLink.style.display = 'none';
      }
    }

  
    renderTemplate() {
      const template = document.createElement('template');
      template.innerHTML = `
        <div class="dropdown-menu fixed-block out-of-sight" id="sizeGuide">
          <div class="size-chart-wrapper">
            <div class="container-fluid">
              <div class="row">
                <div class="col-xxl-8 col-xxl-offset-2 col-md-10 col-md-offset-1 col-sm-12 col-sm-offset-0">
                  <div class="header">
                    <a class="title-wrapper" href="#" id="closeSizeGuideBtn">
                      <div class="button button-medium rounded" id=""></div>
                      <p class="title">Size Guide</p>
                    </a>
                    <div class="unit-picker">
                      <div class="custom-select-wrapper dropdown-link">
                        <select class="custom-select pill rounded-button">
                          <option value="inches">Inches</option>
                          <option value="cms">Cm</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  <div class="size-chart-table">
                    <div id="sizeTableBlock">
                      <table width="100%" id="sizeGuideLabelTable">
                      </table>
                    </div>
                    <div class="chart-actions">
                      <a id="detailedSizeGuideLink" href="" target="_blank" class="button button-medium rounded" id="pdfLink">
                        <p class="">Full guide</p>
                      </a>
                      <a href="/how-to-measure" target="_blank" class="button button-medium rounded" id="guideBtn">
                        <p class="">How to measure</p>
                      </a>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="size-model-wrapper">
            <div class="size-model-details">
              <div class="container-fluid">
                <div class="row">
                  <div class="col-xxl-8 col-xxl-offset-2 col-md-10 col-md-offset-1 col-sm-12 col-sm-offset-0">
                    <div class="section-title">
                      <p class="title" id="modelName">Bharath</p>
                      <p class="footnote title">Size</p>
                    </div>
                    <div class="model-info">
                        <div class="model-measurements">
                            <p class="footnote">Chest</p>
                            <p id="avatarChest" class="footnote">38</p>
                        </div>
                        <div class="model-measurements">
                            <p class="footnote">Waist</p>
                            <p id="avatarWaist" class="footnote">30</p>
                        </div>
                        <div class="model-measurements">
                            <p class="footnote">Bicep</p>
                            <p id="avatarBiceps" class="footnote">15</p>
                        </div>
                        <div class="model-measurements">
                            <p class="footnote">Height</p>
                            <p id="avatarHeight" class="footnote">6'11"</p>
                        </div>
                        <div class="model-size">
                            <h1 id="avatarSize">M</h1>
                        </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="size-model">
              <img id="avatarImage" src=""/>
            </div>
          </div>
        </div>
      `;
      this.appendChild(template.content.cloneNode(true));
    }
  }
  
  window.customElements.define('size-guide', SizeGuide);